import { ICustomRequirements, IRequirement } from '@partsbadger/types';

export type RequirementFlag = 'customer_supplied_material' | 'domestic_quoting' | 'create_2d_files' | 'create_3d_files';

export const REQUIREMENT_FLAG_NAMES: Record<RequirementFlag, string> = {
    customer_supplied_material: 'Customer Supplied Material',
    domestic_quoting: 'Domestic Production Required',
    create_2d_files: 'Create 2D Files',
    create_3d_files: 'Create 3D Files',
};

export interface DefaultRequirement {
    key: string | number;
    label: string;
    value: string | number | null;
    files?: ICustomRequirements['files'] | number[];
}

const requirementKey = (requirement: { key: string | number }) => String(requirement.key);

export const findRequirementByName = (requirements: IRequirement[], name: string) =>
    requirements.find(requirement => requirement.name?.trim().toLowerCase() === name.trim().toLowerCase());

export const buildUpdatedRequirements = (
    checked: boolean,
    requirement: IRequirement | undefined,
    currentRequirements: ICustomRequirements[]
): ICustomRequirements[] => {
    if (!requirement?.id) return currentRequirements;

    const key = String(requirement.id);
    if (checked) {
        if (currentRequirements.some(item => requirementKey(item) === key)) return currentRequirements;
        return [...currentRequirements, { key, label: requirement.name, value: '', files: [] }];
    }

    return currentRequirements.filter(item => requirementKey(item) !== key);
};

export const buildRequirementFlagUpdates = (
    selectedRequirements: ICustomRequirements[],
    requirements: IRequirement[]
): Partial<Record<RequirementFlag, boolean>> => {
    const selectedKeys = new Set(selectedRequirements.map(requirementKey));

    return (Object.keys(REQUIREMENT_FLAG_NAMES) as RequirementFlag[]).reduce<Partial<Record<RequirementFlag, boolean>>>(
        (updates, flag) => {
            const requirement = findRequirementByName(requirements, REQUIREMENT_FLAG_NAMES[flag]);
            if (requirement?.id) {
                updates[flag] = selectedKeys.has(String(requirement.id));
            }
            return updates;
        },
        {}
    );
};

export const classifyQuoteRequirements = (
    selectedRequirements: ICustomRequirements[] = [],
    defaultRequirements: DefaultRequirement[] = []
) => {
    const defaultKeys = new Set(defaultRequirements.map(requirementKey));
    const selectedKeys = new Set(selectedRequirements.map(requirementKey));

    return {
        customerDefaults: selectedRequirements.filter(requirement => defaultKeys.has(requirementKey(requirement))),
        quoteAdditions: selectedRequirements.filter(requirement => !defaultKeys.has(requirementKey(requirement))),
        missingDefaults: defaultRequirements.filter(requirement => !selectedKeys.has(requirementKey(requirement))),
    };
};

export const restoreDefaultRequirement = (
    requirement: DefaultRequirement,
    currentRequirements: ICustomRequirements[]
): ICustomRequirements[] => {
    if (currentRequirements.some(item => requirementKey(item) === requirementKey(requirement))) {
        return currentRequirements;
    }

    return [
        ...currentRequirements,
        {
            key: requirementKey(requirement),
            label: requirement.label,
            value: requirement.value ?? '',
            files: (requirement.files ?? []) as ICustomRequirements['files'],
        },
    ];
};

/** Catalog entries staff can still add: removed customer defaults are offered through Restore instead. */
export const listAddableRequirements = (
    catalog: IRequirement[] = [],
    selectedRequirements: ICustomRequirements[] = [],
    defaultRequirements: DefaultRequirement[] = []
) => {
    const { missingDefaults } = classifyQuoteRequirements(selectedRequirements, defaultRequirements);
    const unavailable = new Set([...selectedRequirements, ...missingDefaults].map(requirementKey));

    return catalog.filter(requirement => !unavailable.has(String(requirement.id)));
};

/** Adds a catalog requirement; a customer default comes back with its saved value and files. */
export const addRequirement = (
    requirement: IRequirement,
    currentRequirements: ICustomRequirements[],
    defaultRequirements: DefaultRequirement[] = []
): ICustomRequirements[] => {
    const customerDefault = defaultRequirements.find(item => requirementKey(item) === String(requirement.id));
    if (customerDefault) return restoreDefaultRequirement(customerDefault, currentRequirements);

    return buildUpdatedRequirements(true, requirement, currentRequirements);
};

export interface QuickAddVisibility {
    materialAndDomestic: boolean;
    files2d: boolean;
    files3d: boolean;
}

/** Shortcuts for the synced quote flags that are shown for this quote and not already on it. */
export const listQuickAddRequirements = (
    catalog: IRequirement[] = [],
    selectedRequirements: ICustomRequirements[] = [],
    defaultRequirements: DefaultRequirement[] = [],
    visibility: QuickAddVisibility
) => {
    const visibleFlags: Record<RequirementFlag, boolean> = {
        customer_supplied_material: visibility.materialAndDomestic,
        domestic_quoting: visibility.materialAndDomestic,
        create_2d_files: visibility.files2d,
        create_3d_files: visibility.files3d,
    };
    const addable = new Set(
        listAddableRequirements(catalog, selectedRequirements, defaultRequirements).map(item => item.id)
    );

    return (Object.keys(REQUIREMENT_FLAG_NAMES) as RequirementFlag[]).flatMap(flag => {
        const requirement = findRequirementByName(catalog, REQUIREMENT_FLAG_NAMES[flag]);
        return visibleFlags[flag] && requirement && addable.has(requirement.id) ? [{ flag, requirement }] : [];
    });
};

export const updateRequirementDetails = (
    currentRequirements: ICustomRequirements[],
    key: string | number,
    details: Partial<Pick<ICustomRequirements, 'value' | 'files'>>
): ICustomRequirements[] =>
    currentRequirements.map(requirement =>
        requirementKey(requirement) === String(key) ? { ...requirement, ...details } : requirement
    );

/** One-line summary of the value or files a requirement carries; null when it takes neither. */
export const describeRequirementDetails = (
    requirement: ICustomRequirements,
    fieldType: IRequirement['field_type'] | string | undefined
): string | null => {
    if (fieldType === 'input' || fieldType === 'inputtext') {
        const value = String(requirement.value ?? '').trim();
        return value || 'No value added';
    }

    if (fieldType === 'file') {
        const count = requirement.files?.length ?? 0;
        if (!count) return 'No files attached';
        return `${count} ${count === 1 ? 'file' : 'files'} attached`;
    }

    return null;
};

export interface RequirementChanges {
    added: string[];
    removed: string[];
    edited: string[];
    count: number;
}

const sameDetails = (a: ICustomRequirements, b: ICustomRequirements) => {
    const fileIds = (requirement: ICustomRequirements) =>
        (requirement.files ?? []).map(file => String(file.id)).join(',');

    return String(a.value ?? '') === String(b.value ?? '') && fileIds(a) === fileIds(b);
};

/** Differences between the saved requirements and the draft staff are about to apply. */
export const describeRequirementChanges = (
    savedRequirements: ICustomRequirements[] = [],
    draftRequirements: ICustomRequirements[] = []
): RequirementChanges => {
    const savedByKey = new Map(savedRequirements.map(requirement => [requirementKey(requirement), requirement]));
    const draftKeys = new Set(draftRequirements.map(requirementKey));

    const added = draftRequirements.filter(requirement => !savedByKey.has(requirementKey(requirement)));
    const edited = draftRequirements.filter(requirement => {
        const saved = savedByKey.get(requirementKey(requirement));
        return saved && !sameDetails(saved, requirement);
    });
    const removed = savedRequirements.filter(requirement => !draftKeys.has(requirementKey(requirement)));

    return {
        added: added.map(requirement => requirement.label),
        removed: removed.map(requirement => requirement.label),
        edited: edited.map(requirement => requirement.label),
        count: added.length + removed.length + edited.length,
    };
};

export const getRequirementChangeState = (
    requirement: ICustomRequirements,
    savedRequirements: ICustomRequirements[] = []
): 'new' | 'edited' | null => {
    const saved = savedRequirements.find(item => requirementKey(item) === requirementKey(requirement));
    if (!saved) return 'new';
    return sameDetails(saved, requirement) ? null : 'edited';
};

/** Counts only: the rows themselves carry the requirement names and their change labels. */
export const summarizeRequirementChanges = ({ added, removed, edited }: RequirementChanges) =>
    [
        added.length ? `${added.length} added` : '',
        removed.length ? `${removed.length} removed` : '',
        edited.length ? `${edited.length} updated` : '',
    ]
        .filter(Boolean)
        .join(' · ');

/**
 * Saved quote additions the draft removes. They stay listed (marked "To be removed") until the draft is saved;
 * removed customer defaults are shown under "Customer defaults not included" instead.
 */
export const listPendingRemovals = (
    savedRequirements: ICustomRequirements[] = [],
    draftRequirements: ICustomRequirements[] = [],
    defaultRequirements: DefaultRequirement[] = []
) => {
    const keep = new Set([...draftRequirements, ...defaultRequirements].map(requirementKey));
    return savedRequirements.filter(requirement => !keep.has(requirementKey(requirement)));
};

/** Rows in their saved order; requirements added in the draft follow in the order they were added. */
export const orderLikeSaved = <T extends { key: string | number }>(
    requirements: T[],
    savedRequirements: ICustomRequirements[] = []
) => {
    const savedIndex = new Map(savedRequirements.map((requirement, index) => [requirementKey(requirement), index]));
    const position = (requirement: T) => savedIndex.get(requirementKey(requirement)) ?? Number.MAX_SAFE_INTEGER;

    return requirements
        .map((requirement, index) => ({ requirement, index }))
        .sort((a, b) => position(a.requirement) - position(b.requirement) || a.index - b.index)
        .map(({ requirement }) => requirement);
};

export const undoRequirementRemoval = (
    requirement: ICustomRequirements,
    savedRequirements: ICustomRequirements[],
    draftRequirements: ICustomRequirements[]
): ICustomRequirements[] => {
    if (draftRequirements.some(item => requirementKey(item) === requirementKey(requirement))) {
        return draftRequirements;
    }

    return orderLikeSaved([...draftRequirements, requirement], savedRequirements);
};

/** Single quote update for the whole draft: the server reprices every line on each save. */
export const buildRequirementsPayload = (requirements: ICustomRequirements[], catalog: IRequirement[]) => ({
    custom_requirements: requirements,
    ...buildRequirementFlagUpdates(requirements, catalog),
});

const SUMMARY_DETAIL_LENGTH = 24;

/** Compact view of the quote requirements for the Parts & Specifications page. */
export const summarizeQuoteRequirements = (
    requirements: ICustomRequirements[] = [],
    defaultRequirements: DefaultRequirement[] = [],
    catalog: IRequirement[] = [],
    visibleCount = 3
) => {
    const detailOf = (requirement: ICustomRequirements) => {
        const fieldType = catalog.find(item => String(item.id) === requirementKey(requirement))?.field_type;

        if (fieldType === 'file') {
            const count = requirement.files?.length ?? 0;
            return count ? `${count} ${count === 1 ? 'file' : 'files'}` : null;
        }

        const value = String(requirement.value ?? '').trim();
        if (!value) return null;
        return value.length > SUMMARY_DETAIL_LENGTH ? `${value.slice(0, SUMMARY_DETAIL_LENGTH)}…` : value;
    };

    return {
        total: requirements.length,
        items: requirements.slice(0, visibleCount).map(requirement => ({
            key: requirementKey(requirement),
            label: requirement.label,
            detail: detailOf(requirement),
        })),
        hiddenCount: Math.max(requirements.length - visibleCount, 0),
        missingDefaultsCount: classifyQuoteRequirements(requirements, defaultRequirements).missingDefaults.length,
    };
};

/**
 * Quote requirements are available only to non-hobbyist contacts. The first page (requirements
 * bar, summary, editor) and the Checkout "Delivery & Requirements" section share this rule, so a
 * requirement can never be saved where the other page would not show it.
 */
export const quoteRequirementsAvailable = (quote?: { contact?: { is_hobbyist?: boolean } | null } | null): boolean =>
    !quote?.contact?.is_hobbyist;
